import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import Auth from './pages/Auth';
import Dashboard from './pages/Dashboard';
import IQACDashboard from './pages/IQACDashboard';
import IQACEvents from './pages/IQACEvents';
import IQACGuests from './pages/IQACGuests';
import Events from './pages/Events';
import Passes from './pages/Passes';
import Gate from './pages/Gate';
import VenueCalendar from './pages/VenueCalendar';
import AoDashboard from './pages/AoDashboard';
import AdminUsers from './pages/AdminUsers';
import AdminLogs from './pages/AdminLogs';
import Approvals from './pages/Approvals';
import Escalations from './pages/Escalations';
import HelpSupport from './pages/HelpSupport';
import GuestList from './pages/GuestList';

function ProtectedRoute({ children }) {
  const { faculty, loading } = useAuth();
  if (loading)
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(180deg, var(--surface-1) 0%, var(--surface-2) 100%)',
        }}
      >
        <div style={{ textAlign: 'center', color: 'var(--text-1)' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.375rem', fontWeight: 800, letterSpacing: '-0.03em', marginBottom: '1rem' }}>
            Guest Portal
          </div>
          <div className="spinner" style={{ margin: '0 auto', width: 34, height: 34 }} />
        </div>
      </div>
    );
  return faculty ? children : <Navigate to="/login" replace />;
}

function AoRoute({ children }) {
  const { faculty } = useAuth();
  if (!faculty || faculty.role !== 'ao') return <Navigate to="/" replace />;
  return children;
}

function IQACRoute({ children }) {
  const { faculty } = useAuth();
  if (!faculty || !['iqac', 'super_admin'].includes(faculty.role)) return <Navigate to="/" replace />;
  return children;
}

function SuperAdminRoute({ children }) {
  const { faculty } = useAuth();
  if (!faculty || faculty.role !== 'super_admin') return <Navigate to="/" replace />;
  return children;
}

function FacultyRoute({ children }) {
  const { faculty } = useAuth();
  if (!faculty || faculty.role !== 'faculty') return <Navigate to="/" replace />;
  return children;
}

function SecurityRoute({ children }) {
  const { faculty } = useAuth();
  if (!faculty || faculty.role !== 'security') return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const { faculty } = useAuth();

  const getDashboardRoute = () => {
    if (!faculty) return '/login';
    if (['iqac', 'super_admin'].includes(faculty.role)) return '/admin/dashboard';
    if (faculty.role === 'ao') return '/ao/dashboard';
    if (faculty.role === 'security') return '/gate/scan';
    return '/faculty/dashboard';
  };

  return (
    <Routes>
      <Route path="/login" element={faculty ? <Navigate to={getDashboardRoute()} replace /> : <Auth />} />

      {/* 1. IQAC / Admin / Super Admin Routes */}
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute>
            <IQACRoute>
              <Layout>
                <IQACDashboard />
              </Layout>
            </IQACRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/events"
        element={
          <ProtectedRoute>
            <IQACRoute>
              <Layout>
                <IQACEvents />
              </Layout>
            </IQACRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/guest-list"
        element={
          <ProtectedRoute>
            <IQACRoute>
              <Layout>
                <GuestList />
              </Layout>
            </IQACRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/guest-passes"
        element={
          <ProtectedRoute>
            <IQACRoute>
              <Layout>
                <IQACGuests />
              </Layout>
            </IQACRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/approvals"
        element={
          <ProtectedRoute>
            <IQACRoute>
              <Layout>
                <Approvals />
              </Layout>
            </IQACRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/escalations"
        element={
          <ProtectedRoute>
            <IQACRoute>
              <Layout>
                <Escalations />
              </Layout>
            </IQACRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/calendar"
        element={
          <ProtectedRoute>
            <IQACRoute>
              <Layout>
                <VenueCalendar />
              </Layout>
            </IQACRoute>
          </ProtectedRoute>
        }
      />

      {/* 2. AO Routes */}
      <Route
        path="/ao/dashboard"
        element={
          <ProtectedRoute>
            <AoRoute>
              <Layout>
                <IQACDashboard />
              </Layout>
            </AoRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ao/approvals"
        element={
          <ProtectedRoute>
            <AoRoute>
              <Layout>
                <Approvals />
              </Layout>
            </AoRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ao/events"
        element={
          <ProtectedRoute>
            <AoRoute>
              <Layout>
                <IQACEvents />
              </Layout>
            </AoRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ao/guest-list"
        element={
          <ProtectedRoute>
            <AoRoute>
              <Layout>
                <GuestList />
              </Layout>
            </AoRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ao/guest-passes"
        element={
          <ProtectedRoute>
            <AoRoute>
              <Layout>
                <IQACGuests />
              </Layout>
            </AoRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ao/escalations"
        element={
          <ProtectedRoute>
            <AoRoute>
              <Layout>
                <Escalations />
              </Layout>
            </AoRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/ao/calendar"
        element={
          <ProtectedRoute>
            <AoRoute>
              <Layout>
                <VenueCalendar />
              </Layout>
            </AoRoute>
          </ProtectedRoute>
        }
      />
      
      {/* 3. Faculty Routes */}
      <Route
        path="/faculty/dashboard"
        element={
          <ProtectedRoute>
            <FacultyRoute>
              <Layout>
                <IQACDashboard />
              </Layout>
            </FacultyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/faculty/events"
        element={
          <ProtectedRoute>
            <FacultyRoute>
              <Layout>
                <IQACEvents />
              </Layout>
            </FacultyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/faculty/guest-list"
        element={
          <ProtectedRoute>
            <FacultyRoute>
              <Layout>
                <GuestList />
              </Layout>
            </FacultyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/faculty/guest-passes"
        element={
          <ProtectedRoute>
            <FacultyRoute>
              <Layout>
                <IQACGuests />
              </Layout>
            </FacultyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/faculty/escalations"
        element={
          <ProtectedRoute>
            <FacultyRoute>
              <Layout>
                <Escalations />
              </Layout>
            </FacultyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/events"
        element={
          <ProtectedRoute>
            <FacultyRoute>
              <Layout>
                <Events />
              </Layout>
            </FacultyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <FacultyRoute>
              <Layout>
                <VenueCalendar />
              </Layout>
            </FacultyRoute>
          </ProtectedRoute>
        }
      />

      {/* 4. Super Admin Exclusive Routes */}
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute>
            <SuperAdminRoute>
              <Layout>
                <AdminUsers />
              </Layout>
            </SuperAdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/logs"
        element={
          <ProtectedRoute>
            <SuperAdminRoute>
              <Layout>
                <AdminLogs />
              </Layout>
            </SuperAdminRoute>
          </ProtectedRoute>
        }
      />

      {/* 5. Security / Gate Management */}
      <Route
        path="/gate"
        element={<Navigate to="/gate/scan" replace />}
      />
      <Route
        path="/gate/scan"
        element={
          <ProtectedRoute>
            <Layout>
              {/* Scan is accessible by Security, IQAC, Super Admin */}
              <Gate defaultTab="scan" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/gate/logs"
        element={
          <ProtectedRoute>
            <Layout>
              {/* Logs is accessible by Security, IQAC, Super Admin */}
              <Gate defaultTab="logs" />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <Layout>
              <HelpSupport />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Redundant/Deprecated Routes - Show "Page Not Found" (or Redirect to Home) */}
      <Route path="/passes" element={<Navigate to="/" replace />} />
      <Route path="/dashboard" element={<Navigate to="/" replace />} />
      <Route path="/escalations" element={<Navigate to="/" replace />} />

      {/* Catch-all */}
      <Route path="/" element={<Navigate to={getDashboardRoute()} replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter basename={process.env.PUBLIC_URL}>
      <AuthProvider>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3500,
            style: {
              fontFamily: 'var(--font-body)',
              fontSize: '15px',
              borderRadius: '12px',
              boxShadow: 'var(--shadow-lg)',
              padding: '12px 16px',
            },
            success: { iconTheme: { primary: '#005fa7', secondary: 'white' } },
            error: { iconTheme: { primary: '#dc2626', secondary: 'white' } },
          }}
        />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
